import { useState } from "react";
import { format } from "date-fns";
import { CheckCircle, XCircle, Clock, RefreshCw } from "lucide-react";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { renewSubscription } from "../../slices/subscriptionSlice";
import ProtectedComponent from "../../components/ProtectedComponent";
import { RoleBasedLayout } from "../../components/RoleBasedLayout";
import PaystackButton from "../../components/PaystackButton";
import { toast } from "react-toastify";

const SubscriptionPage = () => {
  const dispatch = useAppDispatch();
  const subscriptionState = useAppSelector((state) => state.subscription);
  const user = useAppSelector((s) => s.auth.user);
  const {
    subscription,
    permissions,
    style,
    trial,
    active,
    expired,
    hasSubscription,
    error,
  } = subscriptionState;

  const [showRenewModal, setShowRenewModal] = useState(false);

  const renewalAmount = subscription?.bill;
  const billingCycle = subscription?.billingCycle || subscription?.billingInterval || "Monthly";

  const handleRenewalSuccess = async () => {
    try {
      await dispatch(
        renewSubscription({ amount: renewalAmount as string })
      ).unwrap();
      setShowRenewModal(false);
      toast.success("Subscription renewed successfully!");
    } catch (err: any) {
      toast.error(err?.message || "Failed to renew subscription");
    }
  };

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        <XCircle size={40} className="mx-auto mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  if (!hasSubscription || !subscription) {
    return (
      <div className="text-center py-10 text-gray-500">
        <XCircle size={40} className="mx-auto mb-2 text-red-400" />
        <h2 className="text-lg font-semibold">No Active Subscription</h2>
        <p className="text-sm mt-2">You don't have a plan yet. Subscribe to unlock features.</p>
        <button
          onClick={() => (window.location.href = "/pricing")}
          className="mt-4 px-4 py-2 rounded-lg bg-primary text-white"
        >
          View Plans
        </button>
      </div>
    );
  }

  return (
    <RoleBasedLayout>
        <div className="p-6 max-w-2xl mx-auto bg-white shadow-md rounded-2xl">
          <h1 className="text-2xl font-bold mb-6 text-gray-800 text-center">Subscription Details</h1>

          {/* Plan Name */}
          <div
            className="flex items-center justify-center gap-3 mb-6 px-4 py-2 rounded-3xl w-fit mx-auto"
            style={{
              backgroundColor: style?.bgColor ?? "#f3f4f6",
              color: style?.textColor ?? "#111827",
            }}
          >
            <span className="font-semibold">{subscription.packageName}</span>
            {trial && (
              <div className="px-2 py-1 text-xs rounded-3xl bg-white text-primary border border-primary">
                Free Trial
              </div>
            )}
          </div>

          {/* Status */}
          <div className="flex justify-center mb-6">
            {expired ? (
              <div className="flex items-center text-red-500 gap-2">
                <XCircle size={20} />
                <span className="font-semibold">Expired</span>
              </div>
            ) : active ? (
              <div className="flex items-center text-green-600 gap-2">
                <CheckCircle size={20} />
                <span className="font-semibold">Active</span>
              </div>
            ) : (
              <div className="flex items-center text-gray-500 gap-2">
                <Clock size={20} />
                <span className="font-semibold">Inactive</span>
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="p-3 bg-green-100 rounded-lg">
              <p className="text-sm text-gray-500">Start Date</p>
              <p className="text-md font-medium text-gray-800">
                {format(new Date(subscription.startDate as string), "PPpp")}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <p className="text-sm text-gray-500">Expiry Date</p>
              <p className="text-md font-medium text-gray-800">
                {format(new Date(subscription.endDate as string), "PPpp")}
              </p>
            </div>
          </div>

          {/* Auto-Renew Status */}
          {subscription.autoRenew !== null && subscription.autoRenew !== undefined && (
            <div className="flex justify-center mb-6">
              <div className={`flex items-center gap-2 text-sm px-3 py-1 rounded-full ${
                subscription.autoRenew
                  ? "bg-blue-50 text-blue-700"
                  : "bg-gray-100 text-gray-500"
              }`}>
                <RefreshCw size={14} />
                <span>Auto-Renewal: {subscription.autoRenew ? "Enabled" : "Disabled"}</span>
              </div>
            </div>
          )}

          {/* Permissions */}
          <div>
            <h2 className="text-lg font-semibold mb-3 text-gray-700">Plan Permissions</h2>
            <ul className="space-y-2">
              {permissions && (
                <>
                {/* Item Limit */}
                  <li className="flex justify-between border-b py-1">
                    <span>Item Limit</span>
                    <span className="font-medium">{permissions.itemLimit ?? "Unlimited"}</span>
                  </li>


                {/* Employee Limit */}
                  <li className="flex justify-between border-b py-1">
                    <span>Employee Limit</span>
                    <span className="font-medium">{permissions.employeeLimit ?? "Unlimited"}</span>
                  </li>


                {/* Store Limit */}
                  <li className="flex justify-between border-b py-1">
                    <span>Store Limit</span>
                    <span className="font-medium">{permissions.storeLimit ?? "Unlimited"}</span>
                  </li>


                {/* Customer  Limit */}
                  <li className="flex justify-between border-b py-1">
                    <span>Customer Limit</span>
                    <span className="font-medium">{permissions.customerLimit ?? "Unlimited"}</span>
                  </li>


                {/* Analytics */}
                  <li className="flex justify-between border-b py-1">
                    <span>Analytics</span>
                    <span className="font-medium">
                      {permissions.hasAnalytics ? "✅ Enabled" : "❌ Not Available"}
                    </span>
                  </li>


                {/* Notifications */}
                  <li className="flex justify-between border-b py-1">
                    <span>Notifications</span>
                    <span className="font-medium">
                      {permissions.hasNotifications ? "✅ Enabled" : "❌ Not Available"}
                    </span>
                  </li>


                {/* Forecasting */}
                  <li className="flex justify-between border-b py-1">
                    <span>Forecasting</span>
                    <span className="font-medium">
                      {permissions.hasDemandForecasting ? "✅ Enabled" : "❌ Not Available"}
                    </span>
                  </li>
                </>
              )}
            </ul>
          </div>

          <ProtectedComponent
            allowedRoles={["Admin"]}
          >
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setShowRenewModal(true)}
                className="bg-green-600 text-white px-5 py-2 rounded-lg hover:opacity-90 flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Renew Subscription
              </button>
              <button
                onClick={() => (window.location.href = "/plans")}
                className="bg-primary text-white px-5 py-2 rounded-lg hover:opacity-90"
              >
                Manage Subscription
              </button>
            </div>
          </ProtectedComponent>
        </div>

        {/* Renewal Modal */}
        {showRenewModal && (
          <div className="modal">
            <div className="modal-body">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-semibold">Renew Subscription</h3>
                  <XCircle
                    className="hover:cursor-pointer hover:text-red-700"
                    color="red"
                    onClick={() => setShowRenewModal(false)}
                  />
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Package</span>
                      <span className="font-semibold">{subscription.packageName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Billing Cycle</span>
                      <span className="font-semibold capitalize">{billingCycle}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount</span>
                      <span className="font-semibold text-green-700">
                        GH&#x20B5;{renewalAmount ?? "N/A"}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500">
                    Your subscription will be extended by{" "}
                    {billingCycle?.toLowerCase() === "yearly" ? "1 year" : "30 days"}{" "}
                    from {expired ? "today" : "your current expiry date"}.
                  </p>

                  {renewalAmount ? (
                    <PaystackButton
                      email={user?.email as string}
                      amount={renewalAmount}
                      slug={`renewal-${user?.firstname}-${user?.lastname}-${user?.Business?.name}`}
                      onSuccess={handleRenewalSuccess}
                    />
                  ) : (
                    <p className="text-center text-red-500 text-sm">
                      Unable to determine renewal amount. Please contact support.
                    </p>
                  )}

                  <button
                    onClick={() => setShowRenewModal(false)}
                    className="w-full rounded border px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </RoleBasedLayout>
  );
}

export default SubscriptionPage;
